import Konva from 'konva';
import { ZodType } from 'zod';
import { ResolveContext } from '../../../core/runtime/resolve';

/**
 * Context passed to each renderer
 * Contains shared resources and configuration needed for rendering elements
 */
export type RenderContext = {
  /** Base URL for loading assets like images */
  assetHost: string;
  /** Context for resolving static/dynamic values */
  resolveContext?: ResolveContext;
};

/**
 * Definition for a template element type
 * @template TProps - The properties type for this element
 */
export type ElementDefinition<TProps = any> = {
  /** Element type identifier (e.g., 'rect', 'circle', 'text') */
  type: string;
  /** Zod schema for validating element properties - allows input with defaults */
  zodSchema: ZodType<TProps, any, any>;
  /** Default properties for new instances of this element */
  defaultProps: TProps;
  /** Function to render the element as Konva configuration */
  konvaRenderer: (props: TProps, ctx: RenderContext) => Konva.NodeConfig[];
  /** Optional form schema for property editing UI */
  formSchema?: ZodType<any>;
  /** UI component configuration */
  ui?: {
    /** Icon for UI display */
    icon?: string;
    /** Color theme for UI */
    color?: string;
    /** Display name for UI */
    displayName?: string;
    /** Description for UI documentation */
    description?: string;
  };
};

/**
 * Registry for managing element type definitions
 * Provides registration, lookup, and validation for template elements
 */
export class ElementRegistry {
  /** Internal storage for element definitions */
  private definitions = new Map<string, ElementDefinition>();

  /**
   * Register a new element type definition
   * @param def - Element definition to register
   * @throws Error if element type is already registered
   */
  public register<T>(def: ElementDefinition<T>): void {
    if (this.definitions.has(def.type)) {
      throw new Error(`Element type '${def.type}' is already registered`);
    }
    this.definitions.set(def.type, def as ElementDefinition);
  }

  /**
   * Get element definition by type
   * @param type - Element type identifier
   * @returns Element definition or undefined if not found
   */
  public get(type: string): ElementDefinition | undefined {
    return this.definitions.get(type);
  }
  
  /**
   * Check if element type is registered
   * @param type - Element type identifier
   * @returns True if element type is registered
   */
  public has(type: string): boolean {
    return this.definitions.has(type);
  }
  
  /**
   * Get all registered element types
   * @returns Array of registered element type identifiers
   */
  public getTypes(): string[] {
    return Array.from(this.definitions.keys());
  }

  /**
   * Get all registered element definitions
   * @returns Array of all element definitions
   */
  public getAll(): ElementDefinition[] {
    return Array.from(this.definitions.values());
  }

  /**
   * Unregister an element type
   * @param type - Element type identifier
   * @returns True if element was unregistered, false if not found
   */
  public unregister(type: string): boolean {
    return this.definitions.delete(type);
  }

  /**
   * Clear all registered elements
   */
  public clear(): void {
    this.definitions.clear();
  }

  /**
   * Get count of registered elements
   */
  public size(): number {
    return this.definitions.size;
  }
}
